/**
 * AIStatusIndicator.tsx
 * Always-visible AI Coach status indicator
 * Shows current state and allows quick access to settings
 * Responsive: Icon only on mobile, text + icon on desktop
 *
 * P0: Must Fix - AI status visibility
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Settings } from 'lucide-react';
import { useAIConsent } from '../../hooks/useAIConsent';
import { useLocation } from 'wouter';

export const AIStatusIndicator: React.FC = () => {
  const { hasConsented, isLoading } = useAIConsent();
  const [, setLocation] = useLocation();
  const [showTooltip, setShowTooltip] = useState(false);

  // Don't show loading spinner - just hide during load to prevent UI blocking
  if (isLoading) {
    return null;
  }

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {hasConsented ? (
          // AI Active State - Responsive
          <div className="flex items-center gap-2 px-2 md:px-3 py-1.5 bg-gradient-to-r from-purple-600/20 to-pink-600/20 backdrop-blur rounded-full border border-purple-500/30">
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Brain className="w-4 h-4 text-purple-400" />
            </motion.div>
            {/* Text: hidden on mobile (< 768px), visible on desktop */}
            <span className="hidden md:inline text-xs font-medium text-white">
              AI Coach Active
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLocation('/settings');
              }}
              className="ml-0 md:ml-1 text-white/60 hover:text-white transition-colors"
              title="AI Settings"
            >
              <Settings className="w-3 h-3" />
            </button>
          </div>
        ) : (
          // AI Disabled State - Just a subtle greyed-out icon
          <button
            onClick={() => setLocation('/settings')}
            className="group p-2 rounded-full bg-white/5 hover:bg-white/10 backdrop-blur border border-white/10 hover:border-purple-500/30 transition-all"
            title="Enable AI Coach for personalized guidance"
          >
            <Brain className="w-4 h-4 text-white/30 group-hover:text-purple-400 transition-colors" />
          </button>
        )}
      </motion.div>

      {/* Mobile tooltip - only shows on small screens when hovering */}
      <AnimatePresence>
        {showTooltip && hasConsented && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="md:hidden absolute top-full mt-2 right-0 px-3 py-2 bg-black/90 backdrop-blur rounded-lg border border-purple-500/30 whitespace-nowrap z-50 pointer-events-none"
          >
            <span className="text-xs font-medium text-white">AI Coach Active</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIStatusIndicator;
